'use client';

import dynamic from 'next/dynamic';
import { memo } from 'react';
import type { Tip } from '../types/weatherApi';

const LottieLoader = dynamic(() => import('./LottieLoader'), {
  ssr: false,
  loading: () => (
    <span className="w-8 h-8 inline-block rounded bg-slate-700/50" aria-hidden="true" />
  ),
});

function tipKey(tip: Tip) {
  // ключ стабильный даже если порядок изменится
  const text = tip.formattedText?.map((p) => p.text).join('') ?? '';
  return `${tip.animationPath}::${text.slice(0, 40)}`;
}

function WeatherHighlights({ tips }: { tips: Tip[] }) {
  return (
    <section
      aria-labelledby="highlights-title"
      className="p-4 md:p-6 bg-slate-800/50 rounded-xl shadow-xl dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:backdrop-blur-sm dark:border dark:border-slate-700"
    >
      <h2 id="highlights-title" className="text-2xl text-main font-semibold mb-2">
        Сегодняшние подсказки
      </h2>

      {tips.length === 0 ? (
        <p className="text-sm text-gray-400">Нет актуальных подсказок.</p>
      ) : (
        <ul className="space-y-3 text-slate-200">
          {tips.map((tip) => (
            <li key={tipKey(tip)} className="flex items-start gap-3">
              <LottieLoader src={tip.animationPath} className="w-8 h-8 shrink-0 mt-0.5" />
              <div className="text-lg leading-snug">
                {tip.formattedText.map((part, idx) => (
                  <span
                    key={idx}
                    className={[
                      part.bold && 'font-semibold',
                      part.color === 'primary' && 'text-blue-400',
                      part.color === 'danger' && 'text-red-400',
                      part.color === 'info' && 'text-cyan-400',
                      part.size === 'sm' && 'text-sm',
                      part.size === 'lg' && 'text-lg',
                      part.size === 'xl' && 'text-xl',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default memo(WeatherHighlights);
